import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import TermsModal from '../components/TermsModal'

function Signup() {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [acceptanceToken, setAcceptanceToken] = useState(null)
  const { signUp } = useAuth()
  const navigate = useNavigate()

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    // Show Terms of Service modal for acceptance
    setShowTermsModal(true)
  }

  const handleTermsAccept = async (termsData) => {
    setLoading(true)
    setError('')

    try {
      // Step 1: Record terms acceptance
      const termsResponse = await fetch('/.netlify/functions/record-terms-acceptance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          companyName: formData.companyName,
          signatoryName: formData.fullName,
          signatoryTitle: termsData.signatoryTitle,
          acknowledgedOrgBinding: termsData.acknowledgedOrgBinding,
          acknowledgedConveyanceDuty: termsData.acknowledgedConveyanceDuty,
          acknowledgedTerms: termsData.acknowledgedTerms,
        })
      })

      const termsResult = await termsResponse.json()

      if (!termsResponse.ok || termsResult.error) {
        throw new Error(termsResult.error || 'Failed to record terms acceptance')
      }

      const token = termsResult.acceptanceToken
      setAcceptanceToken(token)
      setShowTermsModal(false)

      // Step 2: Create Supabase account
      const authData = await signUp(
        formData.email,
        formData.password,
        formData.fullName,
        formData.companyName
      )

      // Check if signup was successful and we have a user
      if (!authData || !authData.user) {
        throw new Error('Signup failed - no user data returned')
      }

      // Step 3: Create contractor and user records via Netlify Function
      // (Uses service role to bypass RLS)
      const accountResponse = await fetch('/.netlify/functions/create-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: authData.user.id,
          email: formData.email,
          fullName: formData.fullName,
          companyName: formData.companyName,
          acceptanceToken: token
        })
      })

      const accountData = await accountResponse.json()

      if (!accountResponse.ok || accountData.error) {
        throw new Error(accountData.error || accountData.message || 'Failed to create account records')
      }

      const contractorId = accountData.contractorId

      // Step 4: Create Stripe Checkout Session
      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          contractorId: contractorId,
          acceptanceToken: token
        })
      })

      const { url, error: checkoutError } = await response.json()

      if (checkoutError || !url) {
        throw new Error(checkoutError || 'Failed to create checkout session')
      }

      // Step 5: Redirect to Stripe Checkout
      window.location.href = url

    } catch (err) {
      console.error('Signup error:', err)
      setError(err.message || 'Failed to create account')
      setLoading(false)
      setShowTermsModal(false)
    }
  }

  const handleTermsDecline = () => {
    setShowTermsModal(false)
    setError('You must accept the Terms of Service to create an account.')
  }

  if (success) {
    return (
      <div className="container" style={{ paddingTop: '60px' }}>
        <div className="card" style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
          <h2 style={{ fontSize: '24px', marginBottom: '15px' }}>Account Created!</h2>
          <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
            Check your email to verify your account, then sign in.
          </p>
          <p style={{ fontSize: '14px', color: '#95a5a6' }}>
            Redirecting to login...
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <TermsModal
        isOpen={showTermsModal}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
        companyName={formData.companyName}
        signatoryName={formData.fullName}
        email={formData.email}
      />

      <div className="container" style={{ paddingTop: '40px' }}>
        <div className="card" style={{ maxWidth: '550px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '24px',
            marginBottom: '10px',
            textAlign: 'center'
          }}>
            Create Your Krinos Account
          </h2>

          <p style={{ textAlign: 'center', color: '#7f8c8d', marginBottom: '30px' }}>
            Start your HVAC assessment journey
          </p>

          {error && (
            <div className="error">{error}</div>
          )}

          <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: '8px', padding: '15px', marginBottom: '25px' }}>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#14532d', marginBottom: '8px' }}>
              💳 7-Day Free Trial
            </div>
            <p style={{ fontSize: '14px', color: '#15803d', margin: 0 }}>
              Start with a 7-day trial. No charge until your trial ends. $1,299/month includes 500 assessments, then $0.39 per additional assessment.
            </p>
          </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="fullName">Full Name</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              placeholder="John Smith"
              value={formData.fullName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="companyName">Company Name</label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              placeholder="Your HVAC Company"
              value={formData.companyName}
              onChange={handleChange}
              required
            />
            <span className="helper-text">Your contractor business name</span>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange}
              required
            />
            <span className="helper-text">Minimum 6 characters</span>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={loading}
          >
            {loading ? 'Setting up your account...' : 'Review Terms & Start Trial →'}
          </button>

          <p style={{ textAlign: 'center', fontSize: '12px', color: '#94a3b8', marginTop: '12px' }}>
            You'll review our Terms of Service, then be redirected to Stripe to complete payment setup
          </p>
        </form>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <p style={{ color: '#7f8c8d', fontSize: '14px' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '600' }}>
              Sign In
            </Link>
          </p>
        </div>

          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            <Link to="/" style={{ color: '#95a5a6', textDecoration: 'none', fontSize: '14px' }}>
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}

export default Signup
